import React, { useState, useEffect } from 'react';
import { API } from '../../../config/api';
import { useTranslation } from '../../../i18n';

const FlipCardElement = ({
  element,
  isEditor = false,
  canInteract = true,
  onFlip,
  syncedFlipped = null
}) => {
  const { t } = useTranslation();
  const { content } = element;
  const { front, back, flipDirection = 'horizontal' } = content;

  const [isFlipped, setIsFlipped] = useState(content.isFlipped || false);

  // Sync flip state from external source
  useEffect(() => {
    if (syncedFlipped !== null) {
      setIsFlipped(syncedFlipped);
    }
  }, [syncedFlipped]);

  const handleFlip = () => {
    if (isEditor || !canInteract) return;

    const newState = !isFlipped;
    setIsFlipped(newState);

    if (onFlip) {
      onFlip(newState);
    }
  };

  const renderSide = (side, label) => {
    const { type, text, imageUrl, backgroundColor, fontFamily, fontSize } = side;

    return (
      <div
        className="absolute inset-0 rounded-xl flex items-center justify-center p-3 overflow-hidden"
        style={{
          backgroundColor: backgroundColor || '#2A4D25',
          backfaceVisibility: 'hidden'
        }}
      >
        {type === 'image' && imageUrl ? (
          <img
            src={imageUrl.startsWith('/') ? `${API.BASE}${imageUrl}` : imageUrl}
            alt={label}
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        ) : (
          <p
            className="text-white text-center font-medium break-words"
            style={{
              fontFamily: fontFamily || 'Evolventa',
              fontSize: fontSize ? `${fontSize}px` : 'clamp(10px, 3vw, 18px)',
              textShadow: '0 1px 3px rgba(0,0,0,0.3)'
            }}
          >
            {text || label}
          </p>
        )}
      </div>
    );
  };

  const isHorizontal = flipDirection === 'horizontal';

  return (
    <div
      className="w-full h-full perspective-1000"
      onClick={handleFlip}
      style={{
        perspective: '1000px',
        cursor: canInteract && !isEditor ? 'pointer' : 'default'
      }}
    >
      <div
        className="w-full h-full relative transition-transform duration-500"
        style={{
          transformStyle: 'preserve-3d',
          transform: isFlipped
            ? isHorizontal
              ? 'rotateY(180deg)'
              : 'rotateX(180deg)'
            : 'rotateY(0deg)'
        }}
      >
        {/* Front side */}
        <div
          className="absolute inset-0"
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden'
          }}
        >
          {renderSide(front, t('games.flipCard.front'))}
        </div>

        {/* Back side */}
        <div
          className="absolute inset-0"
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: isHorizontal ? 'rotateY(180deg)' : 'rotateX(180deg)'
          }}
        >
          {renderSide(back, t('games.flipCard.back'))}
        </div>
      </div>

      {/* Editor overlay */}
      {isEditor && (
        <div className="absolute inset-0 flex items-end justify-center pb-2 pointer-events-none">
          <div className="bg-black/50 text-white text-xs px-2 py-1 rounded">
            {t('games.flipCard.label')}
          </div>
        </div>
      )}
    </div>
  );
};

export default FlipCardElement;
